import { Clock } from "lucide-react";
import type { PlanPriceHistory } from "@/lib/types";
import PriceHistorySparkline from "./PriceHistorySparkline";

/**
 * Price-history cell for a flight-options row.
 *
 * - ≥2 drawable points → renders the sparkline (unchanged look).
 * - 1 recorded check   → "Tracking" pill (honest: one data point, building history).
 * - 0 recorded checks  → "New" pill (honest: just started tracking).
 *
 * The "New" vs "Tracking" split keys off `summary.checks` (every recorded
 * check), not off the drawn series — a flight checked 40 times today has one
 * day-bucketed point but is emphatically not new.
 *
 * Never fabricates a trend or fake history.
 */
export default function PriceHistoryCell({ history }: { history?: PlanPriceHistory | null }) {
  const finitePoints = (history?.series ?? []).filter((p) => Number.isFinite(p.price));

  if (finitePoints.length >= 2) {
    return <PriceHistorySparkline history={history} stretch />;
  }

  const isNew = (history?.summary?.checks ?? 0) === 0;

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
      <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
      {isNew ? "New" : "Tracking"}
    </span>
  );
}
